/**
 * DemoOnboardingWizard — Vague 113 Wave 1m
 *
 * 5-step welcome wizard shown ONCE after demo signup. Highlights the most
 * common first-actions: create a session, send a quote, manage sign-ins,
 * invite teammates, customise the brand. Each step has a "Go now" button
 * that deep-links into the relevant app section.
 *
 * Persistence: stored under `localStorage.ofm_demo_onboarding_done` so it
 * shows once. Also gated by `/api/demo/status.is_demo` — never appears for
 * paying customers.
 */
import React, { useEffect, useState } from 'react';
import {
  Sparkles, X, ArrowRight, Calendar, FileText, ClipboardCheck,
  Users, Palette, CheckCircle2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

const STORAGE_KEY = 'ofm_demo_onboarding_done';

const STEPS = [
  {
    id: 'session',
    icon: Calendar,
    title: 'Créez votre première session',
    description:
      "Une session représente une formation planifiée avec des dates, un programme et des stagiaires. C'est le cœur d'OFManager.",
    cta: 'Aller aux sessions',
    target: '/app/sessions',
  },
  {
    id: 'quote',
    icon: FileText,
    title: 'Émettez un devis',
    description:
      "Générez un devis PDF prêt à envoyer en moins de 2 minutes. Vous pouvez le faire signer électroniquement par votre client.",
    cta: 'Aller au module Devis',
    target: '/app/devis',
  },
  {
    id: 'attendance',
    icon: ClipboardCheck,
    title: 'Émargements numériques',
    description:
      "Faites signer présence et fin de formation à vos stagiaires directement depuis leur smartphone — adieu les feuilles papier.",
    cta: 'Voir les émargements',
    target: '/app/emargements',
  },
  {
    id: 'team',
    icon: Users,
    title: 'Invitez votre équipe',
    description:
      "OFManager est multi-utilisateurs : invitez vos formateurs et collaborateurs avec des rôles dédiés. Gagnez +7 jours d'essai si vous invitez 2+ membres !",
    cta: "Gérer l'équipe",
    target: '/app/parametres',
  },
  {
    id: 'brand',
    icon: Palette,
    title: 'Personnalisez votre marque',
    description:
      "Logo, couleurs, en-tête de devis & facture, signature email — votre identité visuelle est partout.",
    cta: 'Personnaliser',
    target: '/app/parametres?tab=branding',
  },
];

export default function DemoOnboardingWizard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (localStorage.getItem(STORAGE_KEY) === '1') return;
    api.get('/demo/status')
      .then(({ data }) => {
        if (cancelled) return;
        // Skip if user is not on demo, or if they chose the "blank" mode
        // (the dedicated DemoBlankWizard handles that case).
        if (data?.is_demo && !data?.is_expired && data?.seed_mode !== 'blank') {
          setOpen(true);
        }
      })
      .catch(() => { /* not a demo or no auth — ignore */ });
    return () => { cancelled = true; };
  }, []);

  if (!open) return null;
  const step = STEPS[stepIdx];
  const Icon = step.icon;
  const last = stepIdx === STEPS.length - 1;

  const close = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
  };

  const goToCta = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
    navigate(step.target);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="demo-onboarding-wizard">
      <div className="bg-white rounded-2xl shadow-2xl w-[92%] max-w-[560px] overflow-hidden">
        {/* Header */}
        <div className="brand-gradient p-5 text-white relative">
          <button
            onClick={close}
            aria-label="Fermer"
            data-testid="demo-onboarding-close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5" />
            <div className="text-[11px] uppercase tracking-wider font-semibold opacity-90">
              Bienvenue — Étape {stepIdx + 1} / {STEPS.length}
            </div>
          </div>
          <h2 className="text-[20px] font-bold leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {step.title}
          </h2>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 rounded-xl bg-[#EEF2FF] flex items-center justify-center flex-shrink-0">
              <Icon className="w-6 h-6 text-[#4F46E5]" />
            </div>
            <p className="text-[13.5px] text-[#4A4B63] leading-relaxed">{step.description}</p>
          </div>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-5" data-testid="demo-onboarding-progress">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === stepIdx ? 'w-8 bg-[#4F46E5]' : i < stepIdx ? 'w-1.5 bg-[#4F46E5]/40' : 'w-1.5 bg-[#E5E7EB]'}`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button
              onClick={close}
              data-testid="demo-onboarding-skip"
              className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-[#6B6C85] hover:bg-[#F6F7FB]"
            >
              Passer le tour
            </button>
            <div className="flex items-center gap-2">
              {stepIdx > 0 && (
                <button
                  onClick={() => setStepIdx((s) => Math.max(0, s - 1))}
                  data-testid="demo-onboarding-prev"
                  className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-[#4A4B63] border border-[#EFF0F6] hover:bg-[#FAFAFE]"
                >
                  Précédent
                </button>
              )}
              <button
                onClick={goToCta}
                data-testid="demo-onboarding-cta"
                className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold inline-flex items-center gap-1.5"
              >
                {step.cta} <ArrowRight className="w-3.5 h-3.5" />
              </button>
              {!last && (
                <button
                  onClick={() => setStepIdx((s) => Math.min(STEPS.length - 1, s + 1))}
                  data-testid="demo-onboarding-next"
                  className="h-10 px-3 rounded-xl bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold"
                >
                  Suivant
                </button>
              )}
              {last && (
                <button
                  onClick={close}
                  data-testid="demo-onboarding-finish"
                  className="h-10 px-3 rounded-xl bg-[#DCFCE7] text-[#15803D] text-[12.5px] font-semibold inline-flex items-center gap-1"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Terminé
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
